<script setup>
// Editor de cenário em página cheia, estilo Fin (Fase 4, spec §4.3; telas extras do canvas §1): aberto pelo clique
// num cenário da tela Roteiro. Cenários com node_key (roteiro strict de voz) não chegam aqui — seguem no editor de
// nós da Fatia 1 (a aba Roteiro), que é o modo avançado. Só o gatilho "Mensagem do cliente" tem efeito de runtime
// nesta fase; canais e público persistem para a tela.
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import PolarisScenariosAPI from 'dashboard/api/polaris/scenarios';
import PolarisOutcomesAPI from 'dashboard/api/polaris/outcomes';

import PageLayout from 'dashboard/components-next/polaris/PageLayout.vue';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import { Input } from 'dashboard/components-next/polaris/ui/input';
import { Textarea } from 'dashboard/components-next/polaris/ui/textarea';
import { Label } from 'dashboard/components-next/polaris/ui/label';
import { Switch } from 'dashboard/components-next/polaris/ui/switch';
import { Checkbox } from 'dashboard/components-next/polaris/ui/checkbox';
import {
  RadioGroup,
  RadioGroupItem,
} from 'dashboard/components-next/polaris/ui/radio-group';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'dashboard/components-next/polaris/ui/dropdown-menu';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { isAdmin } = useAdmin();

const assistantId = computed(() => Number(route.params.assistantId));
const scenarioId = computed(() => Number(route.params.scenarioId));

const scenario = ref(null);
const isLoading = ref(true);
const isSaving = ref(false);
const form = ref(null);
const snapshot = ref('');
const instructionEl = ref(null);

const attributes = useMapGetter('attributes/getContactAttributes');
const collectable = computed(() =>
  (attributes.value || []).filter(item => item.polarisCollect)
);
const availableTools = useMapGetter('polarisTools/getRecords');
const outcomes = ref([]);
const templateSource = ref(null);
const sourceDialogRef = ref(null);
const resetDialogRef = ref(null);
const promptPreview = ref('');
const isPreviewLoading = ref(false);

const TRIGGER_OPTIONS = ['inbound', 'inactivity', 'schedule', 'event'];

const dirty = computed(
  () => form.value && JSON.stringify(form.value) !== snapshot.value
);

const detectedTools = computed(() => {
  if (!form.value) return [];
  const found = [
    ...form.value.instruction.matchAll(/\[[^\]]+\]\(tool:\/\/([^/)]+)\)/g),
  ];
  const catalog = availableTools.value || [];
  return [...new Set(found.map(match => match[1]))].map(id => ({
    id,
    label: catalog.find(tool => tool.id === id)?.title || id,
  }));
});

const backToList = () =>
  router.push({
    name: 'polaris_assistants_scenarios_index',
    params: {
      accountId: route.params.accountId,
      assistantId: assistantId.value,
    },
  });

const load = async () => {
  isLoading.value = true;
  try {
    const { data } = await PolarisScenariosAPI.show({
      assistantId: assistantId.value,
      id: scenarioId.value,
    });
    scenario.value = data;
    form.value = {
      title: data.title,
      description: data.description,
      instruction: data.instruction,
      enabled: data.enabled,
      triggerType: data.trigger?.type || 'inbound',
      channels: [...(data.channels || [])],
      audienceText: data.audience?.text || '',
      dataKeys: [...(data.data_keys || [])],
      outcomeKeys: [...(data.outcome_keys || [])],
      examples: (data.examples || []).map(item => ({ ...item })),
    };
    snapshot.value = JSON.stringify(form.value);
  } catch {
    useAlert(t('POLARIS.SCENARIO_EDITOR.API.LOAD_ERROR'));
    backToList();
  } finally {
    isLoading.value = false;
  }
};

const save = async () => {
  isSaving.value = true;
  try {
    await PolarisScenariosAPI.update(
      { assistantId: assistantId.value, id: scenarioId.value },
      {
        title: form.value.title,
        description: form.value.description,
        instruction: form.value.instruction,
        enabled: form.value.enabled,
        trigger: { type: form.value.triggerType },
        channels: form.value.channels,
        audience: { text: form.value.audienceText },
        data_keys: form.value.dataKeys,
        outcome_keys: form.value.outcomeKeys,
        examples: form.value.examples,
      }
    );
    useAlert(t('POLARIS.SCENARIO_EDITOR.API.SAVE_SUCCESS'));
    backToList();
  } catch {
    useAlert(t('POLARIS.SCENARIO_EDITOR.API.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const insertAtCursor = text => {
  const el = instructionEl.value?.$el ?? instructionEl.value;
  const current = form.value.instruction;
  if (!el || typeof el.selectionStart !== 'number') {
    form.value.instruction = `${current}${current.endsWith('\n') ? '' : '\n'}${text}`;
    return;
  }
  const start = el.selectionStart;
  form.value.instruction = `${current.slice(0, start)}${text}${current.slice(start)}`;
};

const insertTool = tool =>
  insertAtCursor(`[${tool.title || tool.id}](tool://${tool.id})`);
const insertData = key => insertAtCursor(`{${key}}`);
const insertOutcome = outcome =>
  insertAtCursor(`[${outcome.title}](outcome://${outcome.key})`);

const toggleIn = (list, value, checked) => {
  const current = new Set(list);
  if (checked) current.add(value);
  else current.delete(value);
  return [...current];
};

const addExample = kind => form.value.examples.push({ kind, text: '' });
const removeExample = index => form.value.examples.splice(index, 1);

const refreshPreview = async () => {
  isPreviewLoading.value = true;
  try {
    const { data } = await PolarisScenariosAPI.promptPreview(
      { assistantId: assistantId.value, id: scenarioId.value },
      {
        title: form.value.title,
        description: form.value.description,
        instruction: form.value.instruction,
      }
    );
    promptPreview.value = data.prompt;
  } catch {
    promptPreview.value = '';
  } finally {
    isPreviewLoading.value = false;
  }
};

// "Voltar ao modelo" só preenche o formulário com o original (fica sujo para revisar e salvar) — não salva nem sai.
const resetToTemplate = () => {
  if (!templateSource.value) return;
  form.value.title = templateSource.value.title;
  form.value.description = templateSource.value.description;
  form.value.instruction = templateSource.value.instruction;
};

const goToPlayground = () =>
  router.push({
    name: 'polaris_assistants_playground_index',
    params: {
      accountId: route.params.accountId,
      assistantId: assistantId.value,
    },
  });

onMounted(async () => {
  await load();
  store.dispatch('attributes/get');
  store.dispatch('polarisTools/getTools');
  // Desfechos e fonte do template são acessórios: uma falha neles não derruba a página nem a prévia.
  try {
    const { data } = await PolarisOutcomesAPI.get({
      assistantId: assistantId.value,
    });
    outcomes.value = data.payload;
  } catch {
    outcomes.value = [];
  }
  if (scenario.value?.from_template) {
    try {
      const source = await PolarisScenariosAPI.templateSource({
        assistantId: assistantId.value,
        id: scenarioId.value,
      });
      templateSource.value = source.data.source;
    } catch {
      templateSource.value = null;
    }
  }
  refreshPreview();
});
</script>

<template>
  <PageLayout
    :header-title="scenario?.title || ''"
    :show-pagination-footer="false"
    :show-know-more="false"
  >
    <template #headerActions>
      <template v-if="templateSource">
        <Button variant="ghost" size="sm" @click="sourceDialogRef.open()">
          {{ t('POLARIS.SCENARIO_EDITOR.VIEW_TEMPLATE') }}
        </Button>
        <Button
          v-if="isAdmin"
          variant="ghost"
          size="sm"
          @click="resetDialogRef.open()"
        >
          {{ t('POLARIS.SCENARIO_EDITOR.RESET_TO_TEMPLATE') }}
        </Button>
      </template>
      <Button
        v-if="isAdmin"
        size="sm"
        :disabled="!dirty || isSaving"
        @click="save"
      >
        {{ t('POLARIS.SCENARIO_EDITOR.SAVE') }}
      </Button>
    </template>

    <template #body>
      <div v-if="isLoading" class="flex justify-center py-10">
        <Spinner />
      </div>
      <div v-else-if="form" class="flex flex-col gap-6">
        <section class="flex flex-col gap-4">
          <div class="flex items-center justify-between gap-3">
            <span
              v-if="templateSource"
              class="rounded-polaris-md bg-polaris-accent px-2 py-0.5 text-xs font-medium text-polaris-accent-foreground"
            >
              {{
                t('POLARIS.SCENARIO_EDITOR.TEMPLATE_BADGE', {
                  key: templateSource.template_key,
                  version: templateSource.template_version,
                })
              }}
            </span>
            <label
              class="ms-auto flex items-center gap-2 text-sm text-polaris-foreground"
            >
              <Switch v-model="form.enabled" :disabled="!isAdmin" />
              {{ t('POLARIS.SCENARIO_EDITOR.ENABLED') }}
            </label>
          </div>
          <div class="flex flex-col gap-1.5">
            <Label for="scenario-title">
              {{ t('POLARIS.SCENARIO_EDITOR.NAME') }}
            </Label>
            <Input
              id="scenario-title"
              v-model="form.title"
              :disabled="!isAdmin"
            />
          </div>
          <div class="flex flex-col gap-1.5">
            <Label for="scenario-description">
              {{ t('POLARIS.SCENARIO_EDITOR.WHEN_TO_USE') }}
            </Label>
            <Textarea
              id="scenario-description"
              v-model="form.description"
              rows="2"
              :disabled="!isAdmin"
            />
            <p class="m-0 text-xs text-polaris-muted-foreground">
              {{ t('POLARIS.SCENARIO_EDITOR.WHEN_TO_USE_HINT') }}
            </p>
          </div>
        </section>

        <section class="flex flex-col gap-3">
          <h2 class="m-0 text-sm font-semibold text-polaris-foreground">
            {{ t('POLARIS.SCENARIO_EDITOR.TRIGGER.TITLE') }}
          </h2>
          <RadioGroup v-model="form.triggerType" class="flex flex-col gap-2">
            <label
              v-for="option in TRIGGER_OPTIONS"
              :key="option"
              class="flex items-center gap-2 text-sm"
              :class="
                option === 'inbound'
                  ? 'text-polaris-foreground'
                  : 'text-polaris-muted-foreground'
              "
            >
              <RadioGroupItem
                :value="option"
                :disabled="option !== 'inbound' || !isAdmin"
              />
              {{ t(`POLARIS.SCENARIO_EDITOR.TRIGGER.${option.toUpperCase()}`) }}
              <span
                v-if="option !== 'inbound'"
                class="rounded-polaris-md bg-polaris-muted px-1.5 py-0.5 text-xs"
              >
                {{ t('POLARIS.SCENARIO_EDITOR.COMING_SOON') }}
              </span>
            </label>
          </RadioGroup>
          <div class="flex flex-col gap-1.5">
            <Label>{{ t('POLARIS.SCENARIO_EDITOR.CHANNELS') }}</Label>
            <label
              v-for="channel in ['chat', 'whatsapp', 'voice']"
              :key="channel"
              class="flex items-center gap-2 text-sm text-polaris-foreground"
            >
              <Checkbox
                :model-value="form.channels.includes(channel)"
                :disabled="!isAdmin"
                @update:model-value="
                  checked =>
                    (form.channels = toggleIn(form.channels, channel, checked))
                "
              />
              {{
                t(`POLARIS.SCENARIO_EDITOR.CHANNEL_${channel.toUpperCase()}`)
              }}
            </label>
          </div>
          <div class="flex flex-col gap-1.5">
            <Label for="scenario-audience">
              {{ t('POLARIS.SCENARIO_EDITOR.AUDIENCE') }}
            </Label>
            <Input
              id="scenario-audience"
              v-model="form.audienceText"
              :disabled="!isAdmin"
              :placeholder="t('POLARIS.SCENARIO_EDITOR.AUDIENCE_HINT')"
            />
          </div>
          <p class="m-0 text-xs text-polaris-muted-foreground">
            {{ t('POLARIS.SCENARIO_EDITOR.SCOPE_HINT') }}
          </p>
        </section>

        <section class="flex flex-col gap-2">
          <div class="flex flex-wrap items-center gap-2">
            <Label for="scenario-instruction" class="flex-1">
              {{ t('POLARIS.SCENARIO_EDITOR.INSTRUCTIONS') }}
            </Label>
            <DropdownMenu v-if="isAdmin">
              <DropdownMenuTrigger as-child>
                <Button variant="outline" size="sm">
                  {{ t('POLARIS.SCENARIO_EDITOR.INSERT_TOOL') }}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem
                  v-for="tool in availableTools"
                  :key="tool.id"
                  @select="insertTool(tool)"
                >
                  {{ tool.title || tool.id }}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu v-if="isAdmin && collectable.length">
              <DropdownMenuTrigger as-child>
                <Button variant="outline" size="sm">
                  {{ t('POLARIS.SCENARIO_EDITOR.INSERT_DATA') }}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem
                  v-for="attribute in collectable"
                  :key="attribute.id"
                  @select="insertData(attribute.attributeKey)"
                >
                  {{ attribute.attributeDisplayName }}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu v-if="isAdmin && outcomes.length">
              <DropdownMenuTrigger as-child>
                <Button variant="outline" size="sm">
                  {{ t('POLARIS.SCENARIO_EDITOR.INSERT_OUTCOME') }}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem
                  v-for="outcome in outcomes"
                  :key="outcome.id"
                  @select="insertOutcome(outcome)"
                >
                  {{ outcome.title }}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <Textarea
            id="scenario-instruction"
            ref="instructionEl"
            v-model="form.instruction"
            rows="12"
            :disabled="!isAdmin"
          />
          <p class="m-0 text-xs text-polaris-muted-foreground">
            {{
              t('POLARIS.SCENARIO_EDITOR.CHAR_COUNT', {
                count: form.instruction.length,
              })
            }}
          </p>
        </section>

        <section v-if="detectedTools.length" class="flex flex-col gap-2">
          <h2 class="m-0 text-sm font-semibold text-polaris-foreground">
            {{ t('POLARIS.SCENARIO_EDITOR.DETECTED_TOOLS') }}
          </h2>
          <div class="flex flex-wrap gap-1.5">
            <span
              v-for="tool in detectedTools"
              :key="tool.id"
              class="rounded-polaris-md px-2 py-0.5 text-xs"
              :class="
                tool.id.startsWith('custom_')
                  ? 'border border-dashed border-polaris-border text-polaris-muted-foreground'
                  : 'bg-polaris-accent text-polaris-accent-foreground'
              "
            >
              {{ tool.label }}
            </span>
          </div>
        </section>

        <section class="flex flex-col gap-3">
          <h2 class="m-0 text-sm font-semibold text-polaris-foreground">
            {{ t('POLARIS.SCENARIO_EDITOR.DATA_AND_OUTCOMES') }}
          </h2>
          <div v-if="collectable.length" class="flex flex-col gap-1.5">
            <Label>{{ t('POLARIS.SCENARIO_EDITOR.DATA_USED') }}</Label>
            <label
              v-for="attribute in collectable"
              :key="attribute.id"
              class="flex items-center gap-2 text-sm text-polaris-foreground"
            >
              <Checkbox
                :model-value="form.dataKeys.includes(attribute.attributeKey)"
                :disabled="!isAdmin"
                @update:model-value="
                  checked =>
                    (form.dataKeys = toggleIn(
                      form.dataKeys,
                      attribute.attributeKey,
                      checked
                    ))
                "
              />
              {{ attribute.attributeDisplayName }}
            </label>
          </div>
          <div v-if="outcomes.length" class="flex flex-col gap-1.5">
            <Label>{{ t('POLARIS.SCENARIO_EDITOR.OUTCOMES_USED') }}</Label>
            <label
              v-for="outcome in outcomes"
              :key="outcome.id"
              class="flex items-center gap-2 text-sm text-polaris-foreground"
            >
              <Checkbox
                :model-value="form.outcomeKeys.includes(outcome.key)"
                :disabled="!isAdmin"
                @update:model-value="
                  checked =>
                    (form.outcomeKeys = toggleIn(
                      form.outcomeKeys,
                      outcome.key,
                      checked
                    ))
                "
              />
              {{ outcome.title }}
            </label>
          </div>
        </section>

        <section class="flex flex-col gap-2">
          <h2 class="m-0 text-sm font-semibold text-polaris-foreground">
            {{ t('POLARIS.SCENARIO_EDITOR.EXAMPLES.TITLE') }}
          </h2>
          <div
            v-for="(example, index) in form.examples"
            :key="index"
            class="flex items-start gap-2"
          >
            <span
              class="mt-2 shrink-0 rounded-polaris-md px-2 py-0.5 text-xs font-medium"
              :class="
                example.kind === 'good'
                  ? 'bg-polaris-success text-polaris-success-foreground'
                  : 'bg-polaris-warning text-polaris-warning-foreground'
              "
            >
              {{
                t(
                  `POLARIS.SCENARIO_EDITOR.EXAMPLES.${example.kind.toUpperCase()}`
                )
              }}
            </span>
            <Input v-model="example.text" :disabled="!isAdmin" class="flex-1" />
            <Button
              v-if="isAdmin"
              variant="ghost"
              size="sm"
              @click="removeExample(index)"
            >
              {{ t('POLARIS.SCENARIO_EDITOR.EXAMPLES.REMOVE') }}
            </Button>
          </div>
          <div v-if="isAdmin" class="flex gap-2">
            <Button variant="outline" size="sm" @click="addExample('good')">
              {{ t('POLARIS.SCENARIO_EDITOR.EXAMPLES.ADD_GOOD') }}
            </Button>
            <Button variant="outline" size="sm" @click="addExample('avoid')">
              {{ t('POLARIS.SCENARIO_EDITOR.EXAMPLES.ADD_AVOID') }}
            </Button>
          </div>
        </section>
      </div>
    </template>

    <template #aside>
      <div
        class="hidden h-full w-96 shrink-0 flex-col border-0 border-l border-solid border-polaris-border bg-polaris-card lg:flex"
      >
        <div
          class="flex items-center justify-between gap-2 border-0 border-b border-solid border-polaris-border px-4 py-3"
        >
          <h2 class="m-0 text-sm font-semibold text-polaris-foreground">
            {{ t('POLARIS.SCENARIO_EDITOR.PREVIEW.TITLE') }}
          </h2>
          <div class="flex gap-1">
            <Button variant="ghost" size="sm" @click="goToPlayground">
              {{ t('POLARIS.SCENARIO_EDITOR.PREVIEW.TEST') }}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              :disabled="isPreviewLoading"
              @click="refreshPreview"
            >
              {{ t('POLARIS.SCENARIO_EDITOR.PREVIEW.REFRESH') }}
            </Button>
          </div>
        </div>
        <p class="m-0 px-4 py-2 text-xs text-polaris-muted-foreground">
          {{ t('POLARIS.SCENARIO_EDITOR.PREVIEW.LOCKED_HINT') }}
        </p>
        <div class="min-h-0 flex-1 overflow-y-auto px-4 py-2">
          <Spinner v-if="isPreviewLoading" class="mx-auto my-6" />
          <pre
            v-else
            class="m-0 whitespace-pre-wrap break-words text-xs text-polaris-foreground font-mono"
          ><code>{{ promptPreview }}</code></pre>
        </div>
      </div>
    </template>

    <Dialog
      v-if="templateSource"
      ref="sourceDialogRef"
      type="edit"
      width="lg"
      :title="t('POLARIS.SCENARIO_EDITOR.VIEW_TEMPLATE')"
      :description="templateSource.instruction"
      :show-cancel-button="false"
      :confirm-button-label="t('POLARIS.SCENARIO_EDITOR.CLOSE')"
      @confirm="sourceDialogRef.close()"
    />
    <Dialog
      v-if="templateSource"
      ref="resetDialogRef"
      type="alert"
      width="md"
      :title="t('POLARIS.SCENARIO_EDITOR.RESET_TO_TEMPLATE')"
      :description="t('POLARIS.SCENARIO_EDITOR.RESET_CONFIRM')"
      :cancel-button-label="t('POLARIS.SCENARIO_EDITOR.CANCEL')"
      :confirm-button-label="t('POLARIS.SCENARIO_EDITOR.RESET_TO_TEMPLATE')"
      @confirm="resetToTemplate"
    />
  </PageLayout>
</template>
